import { faFileArrowUp, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useState } from "react";
import EditableViewer from "../../../components/EditableViewer";
import {
  FileField,
  FileFieldAppendix,
  FormLabel,
  LabelIcon,
  TechnicalMetadataForm,
} from "../../../components/TechnicalForm";
import ToggleButton from "../../../components/ToggleButton";
import {
  generateTechnicalMetadataFromFiles,
  generateTechnicalMetadataFromRepo,
} from "../../../utils/GenerateTechnicalMetadatasheet";

const Generate: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [repoUrl, setRepoUrl] = useState<string>("");
  const [apiOnly, setApiOnly] = useState<boolean>(false);
  const [response, setResponse] = useState<any>(null);

  const isFileDisabled = repoUrl.trim().length > 0;
  const isRepoDisabled = files.length > 0;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFiles(Array.from(event.target.files));
    }
  };

  const clearInputs = () => {
    setFiles([]);
    setRepoUrl("");
    setResponse(null);
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    try {
      let res: { content: string } | null = null;
      if (files.length > 0) {
        res = await generateTechnicalMetadataFromFiles(files, !apiOnly);
      } else if (repoUrl) {
        res = await generateTechnicalMetadataFromRepo(repoUrl, !apiOnly);
      } else {
        return; // Nothing to submit
      }
      setResponse(res);
    } catch (error) {
      alert(`Error generating metadata sheet. ${error}`);
    }
  };

  return (
    <div>
      <h1>Generate Technical Metadata</h1>
      <h2>
        Extract extensive metadata from your source code and generate a DataDesc
        compliant JSON.
      </h2>

      <TechnicalMetadataForm onSubmit={handleSubmit}>
        <div
          style={{
            opacity: isFileDisabled ? 0.5 : 1,
            transition: "opacity 0.2s",
          }}
        >
          <FormLabel>Choose file(s)</FormLabel>
          <FileField>
            <label
              htmlFor="file"
              style={{ cursor: isFileDisabled ? "not-allowed" : "pointer" }}
            >
              <LabelIcon icon={faFileArrowUp} />
              {files.length > 0
                ? `${files.length} file${files.length > 1 ? "s" : ""} selected`
                : "Select and Upload..."}
            </label>
            <input
              type="file"
              id="file"
              multiple
              disabled={isFileDisabled}
              onChange={handleFileChange}
            />
          </FileField>
        </div>

        <div
          style={{ textAlign: "center", fontWeight: "bold", margin: "10px 0" }}
        >
          OR
        </div>

        <div
          style={{
            opacity: isRepoDisabled ? 0.5 : 1,
            transition: "opacity 0.2s",
          }}
        >
          <FormLabel>Enter a repository URL (GitHub/GitLab)</FormLabel>
          <input
            type="text"
            placeholder="https://github.com/user/repo"
            value={repoUrl}
            disabled={isRepoDisabled}
            onChange={(e) => setRepoUrl(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: isRepoDisabled ? "not-allowed" : "text",
            }}
          />
        </div>

        <FileFieldAppendix>
          <ToggleButton
            height={22}
            toggled={apiOnly}
            onToggle={(toggled) => setApiOnly(toggled)}
          />
          <label htmlFor="apiToggle" style={{ marginRight: "auto" }}>
            API-Only
          </label>

          {(files.length > 0 || repoUrl) && (
            <button
              type="button"
              onClick={clearInputs}
              style={{
                padding: "5px 10px",
                marginRight: "10px",
                cursor: "pointer",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
              }}
            >
              <FontAwesomeIcon
                icon={faTrashCan}
                style={{ marginRight: "5px" }}
              />{" "}
              Clear
            </button>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={files.length === 0 && repoUrl.trim() === ""}
            style={{
              padding: "8px 16px",
              borderRadius: "4px",
              border: "none",
              backgroundColor:
                files.length === 0 && repoUrl.trim() === ""
                  ? "#ccc"
                  : "#007bff",
              color: "white",
              cursor:
                files.length === 0 && repoUrl.trim() === ""
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            Process Input
          </button>
        </FileFieldAppendix>
      </TechnicalMetadataForm>

      {response && (
        <EditableViewer
          text={JSON.stringify(response, null, 2)}
          allowDownload
          isExpandable
        />
      )}
    </div>
  );
};

export default Generate;
